import * as Sentry from '@sentry/react-native';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { apiService } from '../services/apiService';
import { DatabaseService } from '../utils/databaseService';
import logger from '../utils/logger';
import supabase from '../utils/supabaseClient';
import { useAuth } from './AuthContext';

/**
 * ProductDetailContext - Manages product detail page data orchestration
 *
 * This context provides centralized state management for product detail data including:
 * - Vehicle data from Supabase
 * - Fit score calculations
 * - Personalized content from Langflow jobs
 * - Loading and error states
 * - Job status tracking
 *
 * @context ProductDetailContext
 */

// Create the context with a default shape
export const ProductDetailContext = createContext({
  // Data states
  vehicleData: null,
  fitScoreData: null,
  personalizedContent: null,
  onboardingData: null,

  // Loading states (per data source)
  isLoadingVehicle: false,
  isLoadingFitScore: false,
  isLoadingPersonalizedContent: false,

  // Error states
  error: null,

  // Response status
  responseStatus: null, // 'COMPLETE' | 'PERSONALIZED_CONTENT_PENDING'

  // Methods
  refetchData: () => {},
  cleanup: () => {},
});

/**
 * ProductDetailProvider - Provider component for product detail data
 *
 * @param {Object} props
 * @param {string} props.model_id - The vehicle model ID to fetch data for
 * @param {React.ReactNode} props.children - Child components
 */
export const ProductDetailProvider = ({ model_id, children }) => {
  const { user } = useAuth();

  // Data states
  const [vehicleData, setVehicleData] = useState(null);
  const [fitScoreData, setFitScoreData] = useState(null);
  const [personalizedContent, setPersonalizedContent] = useState(null);
  const [onboardingData, setOnboardingData] = useState(null);

  // Loading states (per data source)
  const [isLoadingVehicle, setIsLoadingVehicle] = useState(false);
  const [isLoadingFitScore, setIsLoadingFitScore] = useState(false);
  const [isLoadingPersonalizedContent, setIsLoadingPersonalizedContent] = useState(false);

  // Error states
  const [error, setError] = useState(null);

  // Response status - tracks whether content was COMPLETE or PENDING on initial fetch
  const [responseStatus, setResponseStatus] = useState(null);

  // Realtime subscription refs
  const realtimeSubscriptionRef = useRef(null);
  const imagesSubscriptionRef = useRef(null);

  /**
   * Helper function to fetch data with retry logic
   * @param {Function} fetchFn - The fetch function to retry
   * @param {number} retries - Number of retry attempts (default: 3)
   * @param {number} delay - Delay between retries in milliseconds (default: 2000)
   * @returns {Promise<any>} The fetched data
   */
  const fetchWithRetry = useCallback(async (fetchFn, retries = 3, delay = 2000) => {
    for (let i = 0; i < retries; i++) {
      try {
        const data = await fetchFn();
        return data;
      } catch (error) {
        const isLastAttempt = i === retries - 1;

        logger.warn(
          `[ProductDetailContext] Fetch attempt ${i + 1}/${retries} failed:`,
          error.message
        );

        if (isLastAttempt) {
          throw error;
        }

        // Wait before retrying
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }, []);

  /**
   * Categorize error and return user-friendly message
   * @param {Error} error - The error object
   * @param {string} context - Context where error occurred
   * @returns {Object} Error details with user-friendly message and category
   */
  const categorizeError = useCallback((error, context) => {
    const errorMessage = error?.message || '';
    const errorLower = errorMessage.toLowerCase();

    let category = 'UNKNOWN_ERROR';
    let userMessage = 'An unexpected error occurred. Please try again.';

    // Network/Connection errors
    if (
      errorLower.includes('network') ||
      errorLower.includes('fetch') ||
      errorLower.includes('connection') ||
      errorLower.includes('timeout') ||
      (error.name === 'TypeError' && errorLower.includes('failed to fetch'))
    ) {
      category = 'NETWORK_ERROR';
      userMessage =
        'Network connection failed. Please check your internet connection and try again.';
    }
    // Edge function errors
    else if (
      errorLower.includes('cloudflare') ||
      errorLower.includes('edge function') ||
      errorLower.includes('failed with status')
    ) {
      category = 'EDGE_FUNCTION_ERROR';
      userMessage = 'Service temporarily unavailable. Please try again in a moment.';
    }
    // Job creation errors
    else if (
      context === 'JOB_CREATION' ||
      errorLower.includes('job') ||
      errorLower.includes('langflow')
    ) {
      category = 'JOB_CREATION_ERROR';
      userMessage =
        'Failed to generate personalized content. Basic product information is still available.';
    }
    // Content fetch errors after job completion
    else if (context === 'CONTENT_FETCH' || errorLower.includes('personalized content')) {
      category = 'CONTENT_FETCH_ERROR';
      userMessage = 'Failed to load personalized content. Please try refreshing the page.';
    }
    // Data validation errors
    else if (
      errorLower.includes('not found') ||
      errorLower.includes('invalid') ||
      errorLower.includes('missing')
    ) {
      category = 'DATA_ERROR';
      userMessage = 'Product information not found. Please try a different product.';
    }

    return { category, userMessage };
  }, []);

  /**
   * Fetch product detail data from the edge function
   * Handles both COMPLETE and PERSONALIZED_CONTENT_PENDING responses
   * Includes comprehensive error handling and retry logic
   */
  const fetchProductDetailData = useCallback(async () => {
    if (!model_id) {
      logger.warn('[ProductDetailContext] fetchProductDetailData called without model_id');
      return;
    }

    // Don't fetch if there's no authenticated session
    // This prevents 401 errors when navigating to login after logout
    if (!user) {
      logger.warn('[ProductDetailContext] Skipping fetch - no authenticated user session');
      return;
    }

    // Set all loading states to true
    setIsLoadingVehicle(true);
    setIsLoadingFitScore(true);
    setIsLoadingPersonalizedContent(true);
    setError(null);

    try {
      // Call the enhanced get-product-page-details edge function with retry logic
      const response = await fetchWithRetry(
        async () => {
          return await apiService.invokeEdgeFunction('get-product-page-details', {
            body: { model_id },
          });
        },
        3, // 3 retry attempts
        2000 // 2 second delay between retries
      );

      //logger.log(`[ProductDetailContext] Received response status: ${response.status}`);

      // Update vehicle data and images (always present)
      if (response.modelData) {
        // Merge modelImages into vehicleData for easier access
        setVehicleData({
          ...response.modelData,
          images: response.modelImages || [],
        });
        setIsLoadingVehicle(false);
        //logger.log('[ProductDetailContext] Vehicle data and images loaded');
      } else {
        throw new Error('Missing vehicle data in response');
      }

      // Update fit score (always present)
      if (response.fitScore) {
        setFitScoreData(response.fitScore);
        setIsLoadingFitScore(false);
        //logger.log('[ProductDetailContext] Fit score data loaded');
      } else {
        // Fit score is optional, set placeholder object to keep loading modal visible
        logger.warn('[ProductDetailContext] Fit score data missing from response');
        // Set placeholder with data_fidelity so modal stays visible until real data arrives
        setFitScoreData({ data_fidelity: 'placeholder_generation' });
        setIsLoadingFitScore(false);
      }

      // Handle response based on status
      if (response.status === 'COMPLETE') {
        // All data is ready including personalized content
        setResponseStatus('COMPLETE');

        if (response.personalizedContent) {
          setPersonalizedContent(response.personalizedContent);
        }

        // All loading complete
        setIsLoadingPersonalizedContent(false);
      } else {
        // Data not ready yet - subscription will handle updates when data_fidelity becomes 'confirmed'
        logger.log(
          `[ProductDetailContext] Data not ready (status: ${response.status}), subscription will handle updates`
        );
        setResponseStatus(response.status);
        setPersonalizedContent(null);
        setIsLoadingPersonalizedContent(false);
      }
    } catch (err) {
      // Categorize error and get user-friendly message
      const { category, userMessage } = categorizeError(err, 'FETCH');

      // Enhanced error logging with full context
      logger.error('[ProductDetailContext] Error fetching product detail data:', {
        category,
        model_id,
        user_id: user?.id || 'anonymous',
        error_message: err.message,
        error_name: err.name,
        stack: err.stack,
        timestamp: new Date().toISOString(),
      });

      // Capture to Sentry
      Sentry.captureException(err, {
        tags: { operation: 'fetchProductDetailData', service: 'product-detail', category },
        contexts: { product: { model_id }, user: { id: user?.id } },
      });

      // Set user-friendly error message
      setError(userMessage);

      // Set all loading states to false on error
      setIsLoadingVehicle(false);
      setIsLoadingFitScore(false);
      setIsLoadingPersonalizedContent(false);
    }
  }, [model_id, user, categorizeError, fetchWithRetry]);

  /**
   * @deprecated - No longer used. Using model-level subscription instead.
   * Fetch personalized content for a specific job with retry logic
   * This function is intended to be used by the polling hook after job completion
   *
   * @param {string} completedJobId - The job ID to fetch content for
   * @returns {Promise<Object>} The personalized content
   */
  const fetchPersonalizedContentWithRetry = useCallback(
    async (completedJobId) => {
      if (!completedJobId) {
        throw new Error('Job ID is required to fetch personalized content');
      }

      //logger.log(`[ProductDetailContext] Fetching personalized content for job_id: ${completedJobId}`);

      try {
        // Fetch content with retry logic
        const response = await fetchWithRetry(
          async () => {
            return await apiService.invokeEdgeFunction('get-product-page-details', {
              body: { model_id, job_id: completedJobId },
            });
          },
          3, // 3 retry attempts for content fetch
          2000 // 2 second delay between retries
        );

        // Validate response
        if (!response.personalizedContent) {
          throw new Error('Personalized content not found in response');
        }

        // Update context state
        setPersonalizedContent(response.personalizedContent);
        setIsLoadingPersonalizedContent(false);

        // Update fitScoreData if included in response (should have confirmed data_fidelity)
        if (response.fitScore) {
          setFitScoreData(response.fitScore);
          setIsLoadingFitScore(false);
        }

        //logger.log('[ProductDetailContext] Personalized content fetched successfully after job completion');

        return response.personalizedContent;
      } catch (err) {
        // Categorize error with CONTENT_FETCH context
        const { category, userMessage } = categorizeError(err, 'CONTENT_FETCH');

        // Enhanced error logging
        logger.error(
          '[ProductDetailContext] Error fetching personalized content after job completion:',
          {
            category,
            model_id,
            job_id: completedJobId,
            user_id: user?.id || 'anonymous',
            error_message: err.message,
            error_name: err.name,
            stack: err.stack,
            timestamp: new Date().toISOString(),
          }
        );

        // Set error state
        setError(userMessage);
        setIsLoadingPersonalizedContent(false);

        throw err; // Re-throw for caller to handle
      }
    },
    [model_id, user, categorizeError, fetchWithRetry]
  );

  /**
   * Fetch user's onboarding answers
   * Used for cost calculator inputs
   */
  const fetchOnboardingData = useCallback(async () => {
    if (!user?.id) {
      return;
    }

    try {
      const { data, error } = await DatabaseService.getOnboardingAnswers(user.id);

      if (!error && data?.answers) {
        setOnboardingData(data.answers);
        //logger.log('[ProductDetailContext] Onboarding data loaded');
      } else if (error) {
        logger.warn('[ProductDetailContext] Error fetching onboarding data:', error);
      }
    } catch (err) {
      logger.error('[ProductDetailContext] Exception fetching onboarding data:', err);
      Sentry.captureException(err, {
        tags: { operation: 'fetchOnboardingData', service: 'product-detail' },
        contexts: { user: { id: user?.id } },
      });
    }
  }, [user]);

  /**
   * Refetch all data for the current model
   * Wrapper around fetchProductDetailData for external use
   */
  const refetchData = useCallback(() => {
    fetchProductDetailData();
  }, [fetchProductDetailData]);

  /**
   * Cleanup model-level subscription only
   * Used when model-level subscription completes successfully
   */
  const cleanupModelSubscription = useCallback(() => {
    if (realtimeSubscriptionRef.current) {
      //logger.log('[ProductDetailContext] Cleaning up model-level subscription');
      try {
        supabase.removeChannel(realtimeSubscriptionRef.current);
        realtimeSubscriptionRef.current = null;
      } catch (err) {
        logger.error('[ProductDetailContext] Error cleaning up model subscription:', err);
        Sentry.captureException(err, {
          tags: { operation: 'cleanupModelSubscription', service: 'product-detail' },
        });
      }
    }
  }, []);

  /**
   * Cleanup ALL Realtime subscriptions
   * Unsubscribes from both model-level and images channels if active
   * Used on unmount or model_id change
   */
  const cleanupSubscription = useCallback(() => {
    // Cleanup model-level subscription
    cleanupModelSubscription();

    // Cleanup images subscription
    if (imagesSubscriptionRef.current) {
      logger.log('[ProductDetailContext] Cleaning up images subscription');
      try {
        supabase.removeChannel(imagesSubscriptionRef.current);
        imagesSubscriptionRef.current = null;
      } catch (err) {
        logger.error('[ProductDetailContext] Error cleaning up images subscription:', err);
        Sentry.captureException(err, {
          tags: { operation: 'cleanupImagesSubscription', service: 'product-detail' },
        });
      }
    }
  }, [cleanupModelSubscription]);

  /**
   * Set up model-level subscription to user_model_fit_scores table
   * Listens for data_fidelity transitions to 'confirmed'
   * Handles both INSERT (new rows) and UPDATE (existing rows) events
   */
  const setupModelLevelSubscription = useCallback(() => {
    if (!user?.id || !model_id) {
      logger.warn(
        '[ProductDetailContext] setupModelLevelSubscription called without user_id or model_id'
      );
      return;
    }

    // Cleanup any existing model-level subscription first
    cleanupModelSubscription();

    logger.log(
      `[ProductDetailContext] Setting up model-level subscription for user_id=${user.id}, model_id=${model_id}`
    );

    try {
      // RACE CONDITION FIX: Check if channel already exists and remove it
      const channelName = `product-detail-model-${model_id}-user-${user.id}`;
      const existingChannels = supabase.getChannels();
      const existingChannel = existingChannels.find((ch) => ch.topic === `realtime:${channelName}`);
      if (existingChannel) {
        logger.log(
          `[ProductDetailContext] Found existing channel "${channelName}", removing it`
        );
        supabase.removeChannel(existingChannel);
      }

      // Subscribe to user_model_fit_scores for this specific user
      const channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: '*', // Listen to INSERT and UPDATE events
            schema: 'public',
            table: 'user_model_fit_scores',
            filter: `user_id=eq.${user.id}`,
          },
          async (payload) => {
            const updatedScore = payload.new;
            const oldScore = payload.old;

            logger.log('[ProductDetailContext] Model-level realtime event received:', {
              eventType: payload.eventType,
              hasNewData: !!payload.new,
              hasOldData: !!payload.old,
              newDataKeys: payload.new ? Object.keys(payload.new) : [],
              oldDataKeys: payload.old ? Object.keys(payload.old) : [],
              new_model_id: updatedScore?.model_id,
              new_data_fidelity: updatedScore?.data_fidelity,
              old_data_fidelity: oldScore?.data_fidelity,
            });

            // Only process updates for the current model
            if (updatedScore?.model_id !== model_id) {
              logger.log('[ProductDetailContext] Ignoring update for different model:', updatedScore?.model_id);
              return;
            }

            logger.log('[ProductDetailContext] Processing update for current model:', {
              event: payload.eventType,
              model_id: updatedScore.model_id,
              data_fidelity: updatedScore.data_fidelity,
              timestamp: new Date().toISOString(),
            });

            // Check if data_fidelity reached 'confirmed'
            if (updatedScore.data_fidelity === 'confirmed') {
              logger.log(
                `[ProductDetailContext] ✅ Data fidelity confirmed for model_id: ${model_id}, fetching fresh data`
              );

              try {
                // Fetch complete personalized content from API
                logger.log('[ProductDetailContext] Fetching personalized content from API...');

                const response = await fetchWithRetry(
                  async () => {
                    return await apiService.invokeEdgeFunction('get-product-page-details', {
                      body: { model_id },
                    });
                  },
                  3, // 3 retry attempts
                  2000 // 2 second delay between retries
                );

                // Validate response
                if (!response.personalizedContent) {
                  throw new Error('Personalized content not found in response');
                }

                logger.log('[ProductDetailContext] Personalized content fetched successfully:', {
                  has_personalized_rationale: !!response.personalizedContent.personalized_rationale,
                  has_generic_rationale: !!response.personalizedContent.generic_rationale,
                });

                // Update personalized content
                setPersonalizedContent(response.personalizedContent);
                setIsLoadingPersonalizedContent(false);

                // Update fit score if included
                if (response.fitScore) {
                  logger.log('[ProductDetailContext] Fit score updated:', {
                    data_fidelity: response.fitScore.data_fidelity,
                    score_value: response.fitScore.score_value,
                  });

                  setFitScoreData(response.fitScore);
                  setIsLoadingFitScore(false);
                }

                logger.log(
                  '[ProductDetailContext] ✅ State updated: both fitScoreData and personalizedContent set, loading states cleared'
                );

                // Cleanup model-level subscription after successful update (keep images subscription active)
                logger.log('[ProductDetailContext] Cleaning up model-level subscription after successful update');
                cleanupModelSubscription();
              } catch (err) {
                logger.error(
                  '[ProductDetailContext] ❌ Error fetching data after model-level update:',
                  err
                );
                Sentry.captureException(err, {
                  tags: { operation: 'modelLevelSubscriptionHandler', service: 'product-detail' },
                  contexts: { product: { model_id, user_id: user.id } },
                });
                setError('Failed to load updated data. Please try refreshing the page.');
              }
            } else {
              logger.log('[ProductDetailContext] Data fidelity not yet confirmed:', updatedScore.data_fidelity);
            }
          }
        )
        .subscribe((status, err) => {
          if (err) {
            logger.error('[ProductDetailContext] Model-level subscription error:', err);
            setError('Failed to connect to real-time updates. Please try refreshing the page.');
            return;
          }

          if (status === 'SUBSCRIBED') {
            logger.log(
              `[ProductDetailContext] Successfully subscribed to model-level updates for model: ${model_id}`
            );
            // Gap-check: data may have been confirmed while subscription was being set up.
            // If so, the realtime event was missed — fetch immediately to unblock the skeleton.
            supabase
              .from('user_model_fit_scores')
              .select('data_fidelity')
              .eq('user_id', user.id)
              .eq('model_id', model_id)
              .maybeSingle()
              .then(({ data }) => {
                if (data?.data_fidelity === 'confirmed') {
                  logger.log(
                    '[ProductDetailContext] Gap-check: data already confirmed, fetching immediately'
                  );
                  fetchProductDetailData();
                }
              });
          } else if (status === 'CLOSED') {
            logger.log(
              `[ProductDetailContext] Model-level subscription closed for model: ${model_id}`
            );
          }
        });

      // Store channel reference for cleanup
      realtimeSubscriptionRef.current = channel;
    } catch (err) {
      logger.error('[ProductDetailContext] Error setting up model-level subscription:', err);
      Sentry.captureException(err, {
        tags: { operation: 'setupModelLevelSubscription', service: 'product-detail' },
        contexts: { product: { model_id, user_id: user.id } },
      });
      setError('Failed to setup real-time updates. Please try refreshing the page.');
    }
  }, [user, model_id, cleanupModelSubscription, fetchWithRetry, fetchProductDetailData]);

  /**
   * Set up images subscription to vehicle_model_images table
   * Listens for INSERT events when new images are added
   * Automatically updates displayed images without page refresh
   */
  const setupImagesSubscription = useCallback(() => {
    if (!model_id) {
      logger.warn(
        '[ProductDetailContext] setupImagesSubscription called without model_id'
      );
      return;
    }

    try {
      // Check if channel already exists and remove it
      const channelName = `product-detail-images-${model_id}`;
      const existingChannels = supabase.getChannels();
      const existingChannel = existingChannels.find((ch) => ch.topic === `realtime:${channelName}`);
      if (existingChannel) {
        logger.log(
          `[ProductDetailContext] Found existing images channel "${channelName}", removing it`
        );
        supabase.removeChannel(existingChannel);
      }

      // Subscribe to vehicle_model_images for this specific model
      const channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'vehicle_model_images',
            filter: `model_id=eq.${model_id}`,
          },
          async (payload) => {
            logger.log('[ProductDetailContext] New image inserted:', {
              hasNewData: !!payload.new,
              imageId: payload.new?.id,
              angleView: payload.new?.angle_view,
            });

            try {
              // Fetch all updated images for this model
              logger.log('[ProductDetailContext] Fetching updated images...');

              const { data: images, error: fetchError } = await supabase
                .from('vehicle_model_images')
                .select('*')
                .eq('model_id', model_id);

              if (fetchError) {
                throw fetchError;
              }

              logger.log(`[ProductDetailContext] ✅ Fetched ${images?.length || 0} images`);

              // Transform images to match frontend format (image_url -> url)
              const transformedImages = images?.map((img) => ({
                url: img.image_url,
                angle_view: img.angle_view,
                is_default_display: img.is_default_display,
              })) || [];

              logger.log('[ProductDetailContext] Transformed images:', transformedImages);

              // Update vehicleData with new images
              // Note: Update both 'images' and 'imageList' for compatibility with shared mode
              setVehicleData((prev) => {
                if (!prev) return prev;

                return {
                  ...prev,
                  images: transformedImages,
                  imageList: transformedImages, // Also update imageList for shared mode compatibility
                };
              });

              logger.log('[ProductDetailContext] ✅ Images updated in vehicleData state');
            } catch (err) {
              logger.error(
                '[ProductDetailContext] ❌ Error fetching updated images:',
                err
              );
              Sentry.captureException(err, {
                tags: { operation: 'imagesSubscriptionHandler', service: 'product-detail' },
                contexts: { product: { model_id } },
              });
            }
          }
        )
        .subscribe((status, err) => {
          if (err) {
            logger.error('[ProductDetailContext] Images subscription error:', err);
            return;
          }

          if (status === 'SUBSCRIBED') {
            // Successfully subscribed to image updates
          } else if (status === 'CLOSED') {
            // Images subscription closed
          }
        });

      // Store channel reference for cleanup
      imagesSubscriptionRef.current = channel;
    } catch (err) {
      logger.error('[ProductDetailContext] Error setting up images subscription:', err);
      Sentry.captureException(err, {
        tags: { operation: 'setupImagesSubscription', service: 'product-detail' },
        contexts: { product: { model_id } },
      });
    }
  }, [model_id]);

  /**
   * @deprecated - No longer used. Using model-level subscription instead.
   * Setup Realtime subscription for async job status updates
   * Subscribes to async_jobs table filtered by job_id
   * Listens for UPDATE events with status='completed' or 'failed_permanent'
   *
   * @param {string} subscribeJobId - The job ID to subscribe to
   */
  const _setupRealtimeSubscription = useCallback(
    (subscribeJobId) => {
      if (!subscribeJobId) {
        logger.warn('[ProductDetailContext] setupRealtimeSubscription called without job_id');
        return;
      }

      // Cleanup any existing subscription first
      cleanupSubscription();

      //logger.log(`[ProductDetailContext] Setting up Realtime subscription for job_id: ${subscribeJobId}`);

      // Check current job status before setting up subscription to catch race conditions
      // where job completes between API call and subscription setup
      const checkJobStatus = async () => {
        try {
          const { data, error } = await supabase
            .from('async_jobs')
            .select('status')
            .eq('job_id', subscribeJobId)
            .single();

          if (error) {
            logger.error('[ProductDetailContext] Error checking job status:', error);
            return;
          }

          if (data?.status === 'completed') {
            logger.log(
              `[ProductDetailContext] Job ${subscribeJobId} already completed, fetching content immediately`
            );
            await fetchPersonalizedContentWithRetry(subscribeJobId);
            // Don't set up subscription if job is already done
            return true;
          }
        } catch (err) {
          logger.error('[ProductDetailContext] Exception checking job status:', err);
        }
        return false;
      };

      // Check status before setting up subscription
      checkJobStatus().then((isAlreadyDone) => {
        if (isAlreadyDone) {
          return; // Job complete, no need for subscription
        }

        // Job is still pending, set up real-time subscription
        try {
          // Create channel with unique name for this job
          const channel = supabase
            .channel(`product-detail-${subscribeJobId}`)
            .on(
              'postgres_changes',
              {
                event: 'UPDATE',
                schema: 'public',
                table: 'async_jobs',
                filter: `job_id=eq.${subscribeJobId}`,
              },
              async (payload) => {
                //logger.log('[ProductDetailContext] Realtime update received:', payload);

                const updatedJob = payload.new;
                const jobStatus = updatedJob?.status;

                // Handle job completion
                if (jobStatus === 'completed') {
                  //logger.log(`[ProductDetailContext] Job ${subscribeJobId} completed, fetching personalized content`);

                  try {
                    // Trigger content refetch with retry logic
                    await fetchPersonalizedContentWithRetry(subscribeJobId);

                    // Cleanup subscription after successful fetch
                    cleanupSubscription();
                  } catch (err) {
                    logger.error(
                      '[ProductDetailContext] Error fetching content after job completion:',
                      err
                    );
                    Sentry.captureException(err, {
                      tags: {
                        operation: 'realtimeJobCompletionHandler',
                        service: 'product-detail',
                      },
                      contexts: { job: { id: subscribeJobId }, product: { model_id } },
                    });
                    // Error is already set by fetchPersonalizedContentWithRetry
                    // Cleanup subscription even on error
                    cleanupSubscription();
                  }
                }
                // Handle permanent job failure
                else if (jobStatus === 'failed_permanent') {
                  logger.error(`[ProductDetailContext] Job ${subscribeJobId} failed permanently`);

                  setIsLoadingPersonalizedContent(false);

                  // Set user-friendly error message
                  setError(
                    'Failed to generate personalized content. Basic product information is still available.'
                  );

                  // Cleanup subscription
                  cleanupSubscription();
                }
              }
            )
            .subscribe((status, err) => {
              if (err) {
                logger.error('[ProductDetailContext] Realtime subscription error:', err);
                setError('Failed to connect to real-time updates. Please try refreshing the page.');
                setIsLoadingPersonalizedContent(false);
                return;
              }

              if (status === 'SUBSCRIBED') {
                //logger.log(`[ProductDetailContext] Successfully subscribed to job updates for: ${subscribeJobId}`);
              } else if (status === 'CLOSED') {
                //logger.log(`[ProductDetailContext] Subscription closed for job: ${subscribeJobId}`);
              }
            });

          // Store channel reference for cleanup
          realtimeSubscriptionRef.current = channel;
        } catch (err) {
          logger.error('[ProductDetailContext] Error setting up Realtime subscription:', err);
          Sentry.captureException(err, {
            tags: { operation: 'setupRealtimeSubscription', service: 'product-detail' },
            contexts: { job: { id: subscribeJobId } },
          });
          setError('Failed to setup real-time updates. Please try refreshing the page.');
        }
      });
    },
    [cleanupSubscription, fetchPersonalizedContentWithRetry, model_id]
  );

  /**
   * Cleanup function to reset all state
   * Should be called when component unmounts or model_id changes
   */
  const cleanup = useCallback(() => {
    // Cleanup Realtime subscription
    cleanupSubscription();

    // Reset data states
    setVehicleData(null);
    setFitScoreData(null);
    setPersonalizedContent(null);
    setOnboardingData(null);

    // Reset loading states
    setIsLoadingVehicle(false);
    setIsLoadingFitScore(false);
    setIsLoadingPersonalizedContent(false);

    // Reset error state
    setError(null);

    // Reset response status
    setResponseStatus(null);
  }, [cleanupSubscription]);

  /**
   * Effect to fetch data on mount and when model_id changes
   * Cleanup is called when dependencies change to reset state
   *
   * Note: fetchProductDetailData and cleanup are intentionally NOT in the dependency array
   * to prevent re-fetches when their references change. Only model_id should trigger re-fetches.
   */
  useEffect(() => {
    // Cleanup previous state when model_id changes
    cleanup();

    // Fetch data when model_id is available
    if (model_id) {
      fetchProductDetailData();
    }

    // Cleanup on unmount
    return () => {
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model_id]); // Only model_id - fetchProductDetailData and cleanup intentionally excluded (see comment above)

  /**
   * Effect to fetch onboarding data when user is available
   * This runs independently of model_id to ensure calculator has user preferences
   */
  useEffect(() => {
    if (user?.id) {
      fetchOnboardingData();
    }
  }, [user, fetchOnboardingData]);

  /**
   * Effect to set up model-level subscription when data is not ready
   * Subscribes to user_model_fit_scores table and waits for data_fidelity='confirmed'
   */
  useEffect(() => {
    // Only set up subscription if user is logged in and we have both model_id and fitScoreData
    if (!user?.id || !model_id) {
      return;
    }

    // Check if data_fidelity is already 'confirmed' - no subscription needed
    if (fitScoreData?.data_fidelity === 'confirmed') {
      return;
    }

    // Check if we're in a state where personalization is pending or static data is pending
    const shouldSubscribe =
      responseStatus === 'PERSONALIZED_CONTENT_PENDING' ||
      responseStatus === 'STATIC_DATA_PENDING' ||
      (fitScoreData && fitScoreData.data_fidelity !== 'confirmed');

    if (shouldSubscribe) {
      logger.log(
        `[ProductDetailContext] Setting up model-level subscription (responseStatus: ${responseStatus}, data_fidelity: ${fitScoreData?.data_fidelity})`
      );
      setupModelLevelSubscription();
    }

    // Cleanup model-level subscription on unmount or when dependencies change
    return () => {
      cleanupModelSubscription();
    };
  }, [
    user,
    model_id,
    fitScoreData,
    responseStatus,
    setupModelLevelSubscription,
    cleanupModelSubscription,
  ]);

  /**
   * Effect to set up images subscription for realtime image updates
   * Subscribes to vehicle_model_images table to automatically show new images
   */
  useEffect(() => {
    // Only set up subscription if we have a model_id
    if (!model_id) {
      return;
    }

    setupImagesSubscription();

    // Cleanup images subscription on unmount or when model_id changes
    return () => {
      if (imagesSubscriptionRef.current) {
        logger.log('[ProductDetailContext] Cleaning up images subscription (useEffect cleanup)');
        try {
          supabase.removeChannel(imagesSubscriptionRef.current);
          imagesSubscriptionRef.current = null;
        } catch (err) {
          logger.error('[ProductDetailContext] Error cleaning up images subscription:', err);
        }
      }
    };
  }, [model_id, setupImagesSubscription]);

  // Context value object
  const value = {
    // Data states
    vehicleData,
    fitScoreData,
    personalizedContent,
    onboardingData,

    // Loading states
    isLoadingVehicle,
    isLoadingFitScore,
    isLoadingPersonalizedContent,

    // Error states
    error,

    // Response status (for loading type detection)
    responseStatus,

    // Methods
    refetchData,
    cleanup,

    // Internal setters (for use by hooks/effects in subsequent tasks)
    setVehicleData,
    setFitScoreData,
    setPersonalizedContent,
    setOnboardingData,
    setIsLoadingVehicle,
    setIsLoadingFitScore,
    setIsLoadingPersonalizedContent,
    setError,
  };

  return <ProductDetailContext.Provider value={value}>{children}</ProductDetailContext.Provider>;
};

/**
 * Custom hook to access ProductDetailContext
 *
 * @returns {Object} Product detail context value
 * @throws {Error} If used outside ProductDetailProvider
 */
export const useProductDetail = () => {
  const context = useContext(ProductDetailContext);

  if (context === undefined) {
    throw new Error('useProductDetail must be used within a ProductDetailProvider');
  }

  return context;
};
